/**
 * useLocaleCurrency — composable para preferência de país, idioma e moeda.
 *
 * - Persiste preferência em cookie (ld_country, ld_lang, ld_currency) para SSR.
 * - Também espelha no localStorage para leitura rápida no cliente.
 * - GVGmall default é USD; nunca cai em BRL automaticamente.
 * - Não faz conversão de câmbio fixa no frontend.
 */

import { ref, computed, watch } from 'vue'
import {
  MARKETS,
  DEFAULT_MARKET,
  getMarketByCode,
  normalizeLanguage,
  normalizeCurrency,
  type MarketConfig,
  type SupportedLanguage,
  type SupportedCurrencyLower,
} from '~/config/markets'

const STORAGE_KEY = 'gvgmall-market'

function readLocalStorage(): Partial<MarketConfig> | null {
  if (!import.meta.client) return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function writeLocalStorage(partial: Partial<MarketConfig>) {
  if (!import.meta.client) return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(partial))
  } catch {}
}

function detectNavigatorLanguage(): SupportedLanguage {
  if (!import.meta.client) return 'en'
  try {
    const list = Array.isArray(navigator.languages) ? navigator.languages : [navigator.language]
    for (const raw of list) {
      const l = normalizeLanguage(raw)
      if (l) return l
    }
  } catch {}
  return 'en'
}

export function useLocaleCurrency() {
  const countryCookie = useCookie<string | null>('ld_country', { sameSite: 'lax', path: '/' })
  const langCookie = useCookie<string | null>('ld_lang', { sameSite: 'lax', path: '/' })
  const currencyCookie = useCookie<string | null>('ld_currency', { sameSite: 'lax', path: '/' })

  // Local state for reactive selection; initialised from cookies/localStorage.
  const selectedCountry = ref<string>(
    String(countryCookie.value || readLocalStorage()?.countryCode || '').trim().toUpperCase()
  )
  const selectedLanguage = ref<SupportedLanguage>(
    (normalizeLanguage(langCookie.value || readLocalStorage()?.language || detectNavigatorLanguage()) as SupportedLanguage)
  )
  const selectedCurrency = ref<SupportedCurrencyLower>(
    normalizeCurrency(currencyCookie.value || readLocalStorage()?.currencyLower || DEFAULT_MARKET.currencyLower)
  )

  const market = computed<MarketConfig>(() => {
    const byCountry = getMarketByCode(selectedCountry.value)
    // If a language is explicitly selected but the country cookie differs, keep the country cookie
    // unless the country cookie is empty/AUTO.
    if (selectedCountry.value && selectedCountry.value !== 'AUTO') {
      return byCountry
    }
    // If no country cookie, infer from language but keep USD as default for non-BR languages.
    const byLang = MARKETS.find((m) => m.language === selectedLanguage.value)
    return byLang || DEFAULT_MARKET
  })

  const countryCode = computed(() => market.value.countryCode)
  const language = computed(() => market.value.language)
  const locale = computed(() => market.value.locale)
  const currency = computed(() => market.value.currency)
  const currencyLower = computed(() => market.value.currencyLower)
  const isIntl = computed(() => currencyLower.value !== 'brl')

  function persist() {
    countryCookie.value = market.value.countryCode
    langCookie.value = market.value.language
    currencyCookie.value = market.value.currencyLower
    writeLocalStorage({
      code: market.value.code,
      countryCode: market.value.countryCode,
      language: market.value.language,
      locale: market.value.locale,
      currency: market.value.currency,
      currencyLower: market.value.currencyLower,
    })
  }

  function setCountry(code: string) {
    const normalized = String(code || '').trim().toUpperCase()
    const target = getMarketByCode(normalized)
    selectedCountry.value = target.countryCode
    selectedLanguage.value = target.language
    selectedCurrency.value = target.currencyLower
    persist()
  }

  function setLanguage(lang: SupportedLanguage) {
    selectedLanguage.value = lang
    // If current country does not speak this language, switch to a matching market.
    const matching = MARKETS.find((m) => m.language === lang)
    if (matching && selectedCountry.value && selectedCountry.value !== 'AUTO') {
      const current = getMarketByCode(selectedCountry.value)
      if (current.language !== lang) {
        selectedCountry.value = matching.countryCode
      }
    }
    // Currency follows the selected market when no explicit currency override was chosen.
    const target = MARKETS.find((m) => m.language === selectedLanguage.value)
    if (target) {
      selectedCurrency.value = target.currencyLower
    }
    persist()
  }

  function setCurrency(cur: SupportedCurrencyLower) {
    selectedCurrency.value = cur
    // If currency is BRL, force country to BR and language to pt.
    if (cur === 'brl') {
      selectedCountry.value = 'BR'
      selectedLanguage.value = 'pt'
    } else {
      // For non-BRL, keep language if already non-pt; otherwise keep country preference.
      const matching = MARKETS.find((m) => m.currencyLower === cur)
      if (matching && selectedLanguage.value === 'pt') {
        selectedCountry.value = matching.countryCode
        selectedLanguage.value = matching.language
      }
    }
    persist()
  }

  // Sync any external cookie changes (e.g. from other tab) reactively on client.
  if (import.meta.client) {
    watch(
      () => ({
        country: countryCookie.value,
        lang: langCookie.value,
        currency: currencyCookie.value,
      }),
      (next) => {
        if (next.country) selectedCountry.value = next.country
        if (next.lang) selectedLanguage.value = normalizeLanguage(next.lang) as SupportedLanguage
        if (next.currency) selectedCurrency.value = normalizeCurrency(next.currency)
      },
      { immediate: false }
    )
  }

  function formatPrice(value: number, currencyOverride?: SupportedCurrencyLower | string): string {
    const cur = (currencyOverride || currencyLower.value || 'usd').toUpperCase()
    const localeMap: Record<string, string> = {
      USD: 'en-US',
      EUR: 'de-DE',
      BRL: 'pt-BR',
      GBP: 'en-GB',
    }
    return new Intl.NumberFormat(localeMap[cur] || 'en-US', {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value || 0))
  }

  return {
    market,
    countryCode,
    language,
    locale,
    currency,
    currencyLower,
    isIntl,
    selectedCountry,
    selectedLanguage,
    selectedCurrency,
    setCountry,
    setLanguage,
    setCurrency,
    formatPrice,
  }
}
